import React from 'react';
import { Button, Collapse, Tooltip } from 'antd';
import { ShareAltOutlined, SendOutlined } from '@ant-design/icons';

/**
 * Format field name from kebab-case to Title Case
 * @param {string} fieldName - Field name in kebab-case
 * @returns {string} Formatted field name
 */
export const formatFieldName = (fieldName) => {
  if (!fieldName) return '';

  // Custom field name mappings
  const customMappings = {
    'business-tax-identification-number': 'Tax Identification number',
    'business-registration-number-type': 'Registration number type',
    'documentein': 'Employee Identification number',
    'docformationdate': 'Formation date',
    'docdateincorporated': 'Date of Incorporation',
    'docfederaltaxclassification': 'Federal tax classification',
    'docissuedate': 'Issued date',
    'docformid': 'Form ID',
    'policy_expiry_date': 'Policy expire date',
    'policy_number': 'Policy number',
    'type_of_insurance': 'Type of Insurance',
    'insurer_company_name': 'Insurer company name',
    'docnumberofemployees': 'Employee count',
    'docannualrevenue': 'Annual Revenue',
    'docaddress': 'Address',
    // Consolidated address fields (kebab-case from personaChecks)
    'business-physical-address': 'Business Physical Address',
    'business-registered-address': 'Business Registered Address',
    // Consolidated address fields (underscore format from wallet credentials)
    'business_physical_address': 'Business Physical Address',
    'business_registered_address': 'Business Registered Address',
    'business_physical_address_country_code': 'Country',
    'business_registered_address_country_code': 'Country',
  };

  // Handle dynamic address fields with prefixes (e.g., ubo-1-address, control-person-address)
  if (fieldName.match(/^(ubo-\d+-address|control-person-address|form-filler-address)$/)) {
    // Extract prefix and format it nicely
    const parts = fieldName.split('-');
    if (parts[0] === 'ubo') {
      return `UBO ${parts[1]} Address`;
    } else if (parts[0] === 'control' && parts[1] === 'person') {
      return 'Control Person Address';
    } else if (parts[0] === 'form' && parts[1] === 'filler') {
      return 'Form Filler Address';
    }
  }

  // Check if there's a custom mapping first
  if (customMappings[fieldName]) {
    return customMappings[fieldName];
  }

  // Otherwise, apply generic formatting
  return fieldName
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Get display text for report fields
 * @param {Object} field - Field object with fieldName, isVerified, and potentially report data
 * @returns {string} Enhanced display text for report fields
 */
export const getFieldDisplayText = (field) => {
  if (!field) return '';

  const baseName = formatFieldName(field.fieldName);

  // If this is a report field, add status information
  if (field.type === 'report') {
    const result = field.result;
    const status = field.status;

    // If status is ready and result is null, it means the report is verified/clear
    if (status === 'ready' && (result === null || result === '')) {
      return `${baseName} (Clear)`;
    }

    // Otherwise show result-based status
    if (result === 'clear') return `${baseName} (Clear)`;
    if (result === 'consider') return `${baseName} (Review)`;
    if (result === 'flagged') return `${baseName} (Flagged)`;
  }

  return baseName;
};

/**
 * Check if a field name is an address component field
 * @param {string} fieldName - Field name to check
 * @returns {boolean} True if field is an address component
 */
const isAddressComponentField = (fieldName) => {
  if (!fieldName) return false;

  // Address component patterns
  const addressComponentPatterns = [
    /-(street-1|street-2|city|subdivision|postal-code|country-code)$/,
    /_(street_1|street_2|city|subdivision|postal_code|country_code)$/,
    /-address-(street-1|street-2|city|subdivision|postal-code|country-code)$/,
    /_address_(street_1|street_2|city|subdivision|postal_code|country_code)$/
  ];

  return addressComponentPatterns.some(pattern => pattern.test(fieldName));
};

/**
 * Extract address prefix from a field name
 * Examples:
 *   - "ubo-1-address-street-1" → "ubo-1-address"
 *   - "control-person-address-city" → "control-person-address"
 *   - "business-physical-address-street-1" → "business-physical-address"
 * @param {string} fieldName - Field name to extract prefix from
 * @returns {string|null} Address prefix or null if not an address field
 */
const extractAddressPrefix = (fieldName) => {
  if (!fieldName || !isAddressComponentField(fieldName)) return null;

  // Remove the component suffix to get the prefix
  const componentSuffixes = [
    '-street-1', '-street-2', '-city', '-subdivision', '-postal-code', '-country-code',
    '_street_1', '_street_2', '_city', '_subdivision', '_postal_code', '_country_code'
  ];

  for (const suffix of componentSuffixes) {
    if (fieldName.endsWith(suffix)) {
      return fieldName.slice(0, -suffix.length);
    }
  }

  // Handle patterns like "business-physical-address-street-1"
  const match = fieldName.match(/^(.+?-address)-(street-1|street-2|city|subdivision|postal-code|country-code)$/);
  if (match) {
    return match[1];
  }

  return null;
};

/**
 * Consolidate address component fields into single "Address" entries for display
 * This function groups all address component fields (street, city, state, etc.) by their prefix
 * and creates a single consolidated entry for each address.
 *
 * @param {Object} fields - Object with fieldName: isVerified pairs, or Array of {fieldName, isVerified} objects
 * @returns {Array} Array of consolidated field objects for display
 *
 * @example
 * Input: {
 *   "ubo-1-name-first": true,
 *   "ubo-1-address-street-1": true,
 *   "ubo-1-address-city": true,
 *   "ubo-1-address-postal-code": false
 * }
 *
 * Output: [
 *   { fieldName: "ubo-1-name-first", isVerified: true },
 *   { fieldName: "ubo-1-address", isVerified: false, consolidatedFrom: [...] }
 * ]
 */
export const consolidateAddressFieldsForDisplay = (fields) => {
  if (!fields) return [];

  // Convert to array format if it's an object
  let fieldsArray = [];
  if (Array.isArray(fields)) {
    fieldsArray = fields.map(field => {
      if (typeof field === 'object' && field.fieldName !== undefined) {
        return field;
      }
      return null;
    }).filter(Boolean);
  } else if (typeof fields === 'object') {
    fieldsArray = Object.entries(fields).map(([fieldName, isVerified]) => ({
      fieldName,
      isVerified
    }));
  }

  // Group fields by address prefix
  const addressGroups = {}; // { "ubo-1-address": [field1, field2, ...] }
  const nonAddressFields = [];

  for (const field of fieldsArray) {
    const prefix = extractAddressPrefix(field.fieldName);

    if (prefix) {
      // This is an address component - add to group
      if (!addressGroups[prefix]) {
        addressGroups[prefix] = [];
      }
      addressGroups[prefix].push(field);
    } else {
      // Not an address component - keep as is
      nonAddressFields.push(field);
    }
  }

  // Create consolidated address entries
  const consolidatedFields = [...nonAddressFields];

  for (const [addressPrefix, addressFields] of Object.entries(addressGroups)) {
    // An address is considered verified only if ALL its components are verified
    const allVerified = addressFields.every(f => f.isVerified === true);

    // Store the original component fields for later use (e.g., in share/request logic)
    const consolidatedField = {
      fieldName: addressPrefix,
      isVerified: allVerified,
      isConsolidatedAddress: true,
      consolidatedFrom: addressFields.map(f => f.fieldName),
      // Preserve any additional properties from the first field (like type, result, status for reports)
      ...addressFields[0]
    };

    // Override fieldName to be the consolidated one
    consolidatedField.fieldName = addressPrefix;

    consolidatedFields.push(consolidatedField);
  }

  return consolidatedFields;
};

/**
 * Check if any education is verified (for candidates)
 * @param {Array} educations - Array of education objects
 * @returns {number} 100 if any education is verified, 0 otherwise
 */
export const getEducationProgress = (educations) => {
  if (!educations || !Array.isArray(educations) || educations.length === 0) return 0;

  const hasVerifiedEducation = educations.some(edu =>
    edu.verified === true || edu.status === 'verified'
  );

  return hasVerifiedEducation ? 100 : 0;
};

/**
 * Render KYB Business Details section (deprecated - use renderKYBBusinessSubcategories instead)
 * @param {Object} params - Parameters
 * @param {Object} params.personaChecks - PersonaChecks object
 * @param {string} params.documentType - Document type key
 * @param {Object} params.verifyIcon - Verified icon
 * @param {Object} params.unveriffiedIcon - Unverified icon
 * @param {Function} params.handleRequestDocument - Document share handler
 * @param {Object} params.css - CSS modules object
 * @param {boolean} params.isOwnListing - Whether the listing belongs to the current user
 * @returns {JSX.Element}
 */
export const renderKYBBusinessDetails = ({
  personaChecks,
  documentType,
  verifyIcon,
  unveriffiedIcon,
  handleRequestDocument,
  css,
  isOwnListing
}) => {
  const kybData = personaChecks[documentType];
  const hasBusinessDetails = kybData?.businessDetails && Object.keys(kybData.businessDetails).length > 0;
  const hasAnyData = kybData && Object.keys(kybData).length > 0;

  if (!hasBusinessDetails && !hasAnyData) {
    return <p>No verification data available</p>;
  }

  const businessDetails = kybData?.businessDetails || {};

  // Check if businessDetails is structured with subcategories (new format)
  const hasSubcategories = businessDetails.businessIdentity || businessDetails.businessRegistry ||
                          businessDetails.businessOther;

  if (hasSubcategories) {
    // New format: render using subcategories
    return renderKYBBusinessSubcategories({
      personaChecks,
      documentType,
      verifyIcon,
      unveriffiedIcon,
      handleRequestDocument,
      css,
      isOwnListing
    });
  }

  // Old format: render flat list (backward compatibility)
  const businessFields = businessDetails;

  // Extract only business detail fields (excluding nested objects like beneficialOwners, authorizedPersons)
  const businessDetailsOnly = { ...businessFields };
  delete businessDetailsOnly.beneficialOwners;
  delete businessDetailsOnly.authorizedPersons;

  // Consolidate address fields for display
  const consolidatedFields = consolidateAddressFieldsForDisplay(businessDetailsOnly);

  return (
    <div>
      <h6>Business Identity and Registry Document
        {isOwnListing !== null && (
          <Tooltip title={isOwnListing ? "Share" : "Request"}>
            <Button
              color="default"
              variant="filled"
              shape="circle"
              icon={isOwnListing ? <ShareAltOutlined /> : <SendOutlined />}
              className={css.verifyShare}
              onClick={() => handleRequestDocument('Business Identity and Registry', kybData, 'business-identity', isOwnListing, businessDetailsOnly)}
            />
          </Tooltip>
        )}
      </h6>
      <Collapse
        ghost
        size="small"
        expandIconPosition="end"
        items={[{
          key: 'business-details-fields',
          label: <>View Details</>,
          children: (
            <ul style={{ paddingLeft: '0', margin: '0' }}>
              {consolidatedFields.map((field, index) => {
                return (
                  <li key={index}>
                    <img src={field.isVerified ? verifyIcon : unveriffiedIcon} title={field.isVerified ? "Verified" : "Unverified"} width="15px" />
                    {' '} {formatFieldName(field.fieldName)}
                  </li>
                );
              })}
            </ul>
          )
        }]}
      />
    </div>
  );
};

/**
 * Render KYB Business Subcategories section (new structure)
 * @param {Object} params - Parameters
 * @param {Object} params.personaChecks - PersonaChecks object
 * @param {string} params.documentType - Document type key
 * @param {Object} params.verifyIcon - Verified icon
 * @param {Object} params.unveriffiedIcon - Unverified icon
 * @param {Function} params.handleRequestDocument - Document share handler
 * @param {Object} params.css - CSS modules object
 * @param {boolean} params.isOwnListing - Whether the listing belongs to the current user
 * @returns {JSX.Element}
 */
export const renderKYBBusinessSubcategories = ({
  personaChecks,
  documentType,
  verifyIcon,
  unveriffiedIcon,
  handleRequestDocument,
  css,
  isOwnListing
}) => {
  const kybData = personaChecks[documentType];
  const businessDetails = kybData?.businessDetails;

  if (!businessDetails) {
    return <p>No verification data available</p>;
  }

  const subcategories = [
    { key: 'businessIdentity', label: 'Business Identity', data: businessDetails.businessIdentity || [] },
    { key: 'businessRegistry', label: 'Business Registry', data: businessDetails.businessRegistry || [] },
    { key: 'businessOther', label: 'Business Information', data: businessDetails.businessOther || [] }
  ];

  return (
    <>
      {subcategories.map((subcategory, subcategoryIndex) => {
        if (subcategory.data.length === 0) {
          return null; // Don't render empty subcategories
        }

        // Consolidate address fields for display
        const consolidatedFields = consolidateAddressFieldsForDisplay(subcategory.data);

        // Convert array format to object format for handleRequestDocument
        const subcategoryFieldsAsObject = {};
        subcategory.data.forEach(({ fieldName, isVerified }) => {
          subcategoryFieldsAsObject[fieldName] = isVerified;
        });

        return (
          <div key={subcategoryIndex} style={{ marginBottom: '20px' }}>
            <h6>{subcategory.label}
              {isOwnListing !== null && (
                <Tooltip title={isOwnListing ? "Share" : "Request"}>
                  <Button
                    color="default"
                    variant="filled"
                    shape="circle"
                    icon={isOwnListing ? <ShareAltOutlined /> : <SendOutlined />}
                    className={css.verifyShare}
                    onClick={() => handleRequestDocument(subcategory.label, subcategoryFieldsAsObject, subcategory.key, isOwnListing, subcategoryFieldsAsObject)}
                  />
                </Tooltip>
              )}
            </h6>
            <Collapse
              ghost
              size="small"
              expandIconPosition="end"
              items={[{
                key: `${subcategory.key}-fields`,
                label: <>View Details</>,
                children: (
                  <ul style={{ paddingLeft: '0', margin: '0' }}>
                    {consolidatedFields.map((field, fieldIndex) => {
                      return (
                        <li key={fieldIndex}>
                          <img src={field.isVerified ? verifyIcon : unveriffiedIcon} title={field.isVerified ? "Verified" : "Unverified"} width="15px" />
                          {' '} {getFieldDisplayText(field)}
                        </li>
                      );
                    })}
                  </ul>
                )
              }]}
            />
          </div>
        );
      })}
    </>
  );
};

/**
 * Render KYB Beneficial Owners section
 * @param {Object} params - Parameters
 * @param {Object} params.personaChecks - PersonaChecks object
 * @param {string} params.documentType - Document type key
 * @param {Object} params.verifyIcon - Verified icon
 * @param {Object} params.unveriffiedIcon - Unverified icon
 * @param {Function} params.handleRequestDocument - Document share handler
 * @param {Object} params.css - CSS modules object
 * @param {boolean} params.isOwnListing - Whether the listing belongs to the current user
 * @returns {JSX.Element}
 */
export const renderKYBBeneficialOwners = ({
  personaChecks,
  documentType,
  verifyIcon,
  unveriffiedIcon,
  handleRequestDocument,
  css,
  isOwnListing
}) => {
  const kybData = personaChecks[documentType];
  const beneficialOwners = kybData?.beneficialOwners;

  if (!beneficialOwners || beneficialOwners.length === 0) {
    return <p>No verification data available</p>;
  }

  return (
    <>
      {beneficialOwners.map((owner, ownerIndex) => {
        // Consolidate address fields for display
        const consolidatedFields = consolidateAddressFieldsForDisplay(owner.fields);
        // Outside the company an owner arrives without a name: their details are shared only
        // through the one-time view link, so they are numbered instead.
        const ownerLabel = owner.name || `Beneficial owner ${ownerIndex + 1}`;

        return (
          <div key={ownerIndex} style={{ marginBottom: '20px' }}>
            <h6>{ownerLabel}
              {isOwnListing !== null && (
                <Tooltip title={isOwnListing ? "Share" : "Request"}>
                  <Button
                    color="default"
                    variant="filled"
                    shape="circle"
                    icon={isOwnListing ? <ShareAltOutlined /> : <SendOutlined />}
                    className={css.verifyShare}
                    onClick={() => handleRequestDocument(`Beneficial Owner - ${owner.name || ownerIndex + 1}`, owner.fields, 'beneficial-owner', isOwnListing, owner.fields)}
                  />
                </Tooltip>
              )}
            </h6>
            <Collapse
              ghost
              size="small"
              expandIconPosition="end"
              items={[{
                key: `ubo-${ownerIndex}-fields`,
                label: <>View Details</>,
                children: (
                  <ul style={{ paddingLeft: '0', margin: '0' }}>
                    {consolidatedFields.map((field, fieldIndex) => (
                      <li key={fieldIndex}>
                        <img src={field.isVerified ? verifyIcon : unveriffiedIcon} title={field.isVerified ? "Verified" : "Unverified"} width="15px" />
                        {' '} {formatFieldName(field.fieldName)}
                      </li>
                    ))}
                  </ul>
                )
              }]}
            />
          </div>
        );
      })}
    </>
  );
};

/**
 * Render KYB Authorized Persons section
 * @param {Object} params - Parameters
 * @param {Object} params.personaChecks - PersonaChecks object
 * @param {string} params.documentType - Document type key
 * @param {Object} params.verifyIcon - Verified icon
 * @param {Object} params.unveriffiedIcon - Unverified icon
 * @param {Function} params.handleRequestDocument - Document share handler
 * @param {Object} params.css - CSS modules object
 * @param {boolean} params.isOwnListing - Whether the listing belongs to the current user
 * @returns {JSX.Element}
 */
export const renderKYBAuthorizedPersons = ({
  personaChecks,
  documentType,
  verifyIcon,
  unveriffiedIcon,
  handleRequestDocument,
  css,
  isOwnListing
}) => {
  const kybData = personaChecks[documentType];
  const authorizedPersons = kybData?.authorizedPersons;

  if (!authorizedPersons || authorizedPersons.length === 0) {
    return <p>No verification data available</p>;
  }

  return (
    <>
      {authorizedPersons.map((person, personIndex) => {
        const roleLabel = person.role === 'control-person' ? 'Control Person' : 'Form Filler';

        // Consolidate address fields for display
        const consolidatedFields = consolidateAddressFieldsForDisplay(person.fields);
        // Outside the company a person arrives without a name, and is labelled by role instead.
        const personName = person.name || `${roleLabel} ${personIndex + 1}`;

        return (
          <div key={personIndex} style={{ marginBottom: '20px' }}>
            <h6>{person.name ? `${person.name} (${roleLabel})` : personName}
              {isOwnListing !== null && (
                <Tooltip title={isOwnListing ? "Share" : "Request"}>
                  <Button
                    color="default"
                    variant="filled"
                    shape="circle"
                    icon={isOwnListing ? <ShareAltOutlined /> : <SendOutlined />}
                    className={css.verifyShare}
                    onClick={() => handleRequestDocument(`${personName} - ${roleLabel}`, person.fields, 'authorized-person', isOwnListing, person.fields)}
                  />
                </Tooltip>
              )}
            </h6>
            <Collapse
              ghost
              size="small"
              expandIconPosition="end"
              items={[{
                key: `authorized-${personIndex}-fields`,
                label: <>View Details</>,
                children: (
                  <ul style={{ paddingLeft: '0', margin: '0' }}>
                    {consolidatedFields.map((field, fieldIndex) => (
                      <li key={fieldIndex}>
                        <img src={field.isVerified ? verifyIcon : unveriffiedIcon} title={field.isVerified ? "Verified" : "Unverified"} width="15px" />
                        {' '} {formatFieldName(field.fieldName)}
                      </li>
                    ))}
                  </ul>
                )
              }]}
            />
          </div>
        );
      })}
    </>
  );
};

/**
 * Render KYB Insurance and Reports section (fourth column)
 * @param {Object} params - Parameters
 * @param {Object} params.personaChecks - PersonaChecks object
 * @param {string} params.documentType - Document type key
 * @param {Object} params.verifyIcon - Verified icon
 * @param {Object} params.unveriffiedIcon - Unverified icon
 * @param {Function} params.handleRequestDocument - Document share handler
 * @param {Object} params.css - CSS modules object
 * @param {boolean} params.isOwnListing - Whether the listing belongs to the current user
 * @returns {JSX.Element}
 */
export const renderKYBInsuranceAndReports = ({
  personaChecks,
  documentType,
  verifyIcon,
  unveriffiedIcon,
  handleRequestDocument,
  css,
  isOwnListing
}) => {
  const kybData = personaChecks[documentType];
  const insuranceAndReports = kybData?.insuranceAndReports;

  if (!insuranceAndReports || insuranceAndReports.length === 0) {
    return <p>No verification data available</p>;
  }

  // Consolidate address fields for display
  const consolidatedFields = consolidateAddressFieldsForDisplay(insuranceAndReports);

  // Convert array format to object format for handleRequestDocument
  const fieldsAsObject = {};
  insuranceAndReports.forEach(({ fieldName, isVerified }) => {
    fieldsAsObject[fieldName] = isVerified;
  });

  return (
    <div>
      <h6>Insurance and Reports
        {isOwnListing !== null && (
          <Tooltip title={isOwnListing ? "Share" : "Request"}>
            <Button
              color="default"
              variant="filled"
              shape="circle"
              icon={isOwnListing ? <ShareAltOutlined /> : <SendOutlined />}
              className={css.verifyShare}
              onClick={() => handleRequestDocument('Insurance and Reports', fieldsAsObject, 'insurance-reports', isOwnListing, fieldsAsObject)}
            />
          </Tooltip>
        )}
      </h6>
      <Collapse
        ghost
        size="small"
        expandIconPosition="end"
        items={[{
          key: 'insurance-reports-fields',
          label: <>View Details</>,
          children: (
            <ul style={{ paddingLeft: '0', margin: '0' }}>
              {consolidatedFields.map((field, fieldIndex) => {
                return (
                  <li key={fieldIndex}>
                    <img src={field.isVerified ? verifyIcon : unveriffiedIcon} title={field.isVerified ? "Verified" : "Unverified"} width="15px" />
                    {' '} {getFieldDisplayText(field)}
                  </li>
                );
              })}
            </ul>
          )
        }]}
      />
    </div>
  );
};
